<!--
  @component ItemContextMenu
  @description Context menu for item actions
-->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { t } from '@/i18n';
  import { itemContextMenu, hideItemContextMenu, showExportModal } from '@stores/uiStore';
  import { selectedLibraryId, selectedLibraryType } from '@stores/libraryStore';
  import { selectItem, selectedItems, selectedItemKeys } from '@stores/itemStore';
  import { itemActions } from '@services/itemActionsService';
  import { enableMenuKeyboardNavigation, createEscapeListener } from '@/utils';

  let menuElement: HTMLElement;
  let cleanupKeyboardNav: (() => void) | null = null;

  // Centralized keyboard handling for Escape key
  const escapeHandler = createEscapeListener(hideItemContextMenu);

  // Menu items configuration
  const menuItems = [
    { action: 'view', icon: '👁️', labelKey: 'detailsPanel.viewMode' },
    { action: 'edit', icon: '✏️', labelKey: 'item.editItem', shortcut: 'Double-clic' },
    { separator: true },
    { action: 'duplicate', icon: '📋', labelKey: 'common.duplicate' },
    { action: 'export', icon: '📤', labelKey: 'common.export' },
    { separator: true },
    { action: 'copyTitle', icon: '📝', labelKey: 'export.copyTitle' },
    { action: 'copyUrl', icon: '🔗', labelKey: 'export.copyUrl' },
    { separator: true },
    { action: 'delete', icon: '🗑️', labelKey: 'common.delete', danger: true },
  ];

  // Handle menu action
  async function handleAction(action: string) {
    const item = $itemContextMenu.item;
    if (!item) return;

    // Store references before hiding menu (for delete action)
    const libraryId = $selectedLibraryId;
    const libraryType = $selectedLibraryType;

    switch (action) {
      case 'view':
        selectItem(item);
        hideItemContextMenu();
        break;
      case 'edit':
        selectItem(item);
        hideItemContextMenu();
        // Trigger edit mode (handled by parent)
        break;
      case 'duplicate':
        hideItemContextMenu();
        if (libraryId && libraryType) {
          await itemActions.duplicateItem(libraryId, libraryType, item);
        }
        break;
      case 'export': {
        // If item is part of multi-selection, export all selected items
        const isInSelection =
          $selectedItemKeys.has(item.key) || $selectedItems.some((i) => i.key === item.key);
        hideItemContextMenu();
        if (isInSelection && $selectedItems.length > 1) {
          showExportModal($selectedItems);
        } else {
          showExportModal([item]);
        }
        break;
      }
      case 'copyTitle':
        hideItemContextMenu();
        await itemActions.copyTitle(item);
        break;
      case 'copyUrl':
        hideItemContextMenu();
        await itemActions.copyUrl(item);
        break;
      case 'delete': {
        // Check if item is part of multi-selection
        const isInDeleteSelection =
          $selectedItemKeys.has(item.key) || $selectedItems.some((i) => i.key === item.key);
        const itemsToDelete =
          isInDeleteSelection && $selectedItems.length > 1 ? $selectedItems : [item];

        // Hide menu first, then show confirm dialog
        hideItemContextMenu();
        if (libraryId && libraryType) {
          const count = itemsToDelete.length;
          const message =
            count > 1
              ? $t('item.confirmDeleteMultiple', { count })
              : $t('item.confirmDeleteMessage');

          if (confirm(message)) {
            if (count > 1) {
              await itemActions.deleteItems(libraryId, libraryType, itemsToDelete);
            } else {
              await itemActions.deleteItem(libraryId, libraryType, item);
            }
          }
        }
        break;
      }
      default:
        hideItemContextMenu();
    }
  }

  // Adjust position to stay in viewport
  function adjustPosition() {
    if (!menuElement || !$itemContextMenu.visible) return;

    const rect = menuElement.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let x = $itemContextMenu.x;
    let y = $itemContextMenu.y;

    if (x + rect.width > windowWidth) {
      x = windowWidth - rect.width - 10;
    }

    if (y + rect.height > windowHeight) {
      y = windowHeight - rect.height - 10;
    }

    menuElement.style.left = `${x}px`;
    menuElement.style.top = `${y}px`;
  }

  onMount(() => {
    escapeHandler.attach();
    adjustPosition();
  });

  onDestroy(() => {
    escapeHandler.detach();
    cleanupKeyboardNav?.();
  });

  // Setup keyboard navigation and focus when menu opens
  $: if ($itemContextMenu.visible && menuElement) {
    requestAnimationFrame(() => {
      adjustPosition();
      // Enable arrow key navigation
      cleanupKeyboardNav?.();
      cleanupKeyboardNav = enableMenuKeyboardNavigation(menuElement, '[role="menuitem"]');
    });
  } else {
    cleanupKeyboardNav?.();
    cleanupKeyboardNav = null;
  }
</script>

{#if $itemContextMenu.visible && $itemContextMenu.item}
  <div
    class="context-menu"
    bind:this={menuElement}
    style="left: {$itemContextMenu.x}px; top: {$itemContextMenu.y}px;"
    role="menu"
    aria-label={$t('a11y.itemActions')}
    tabindex="-1"
  >
    {#each menuItems as item}
      {#if item.separator}
        <div class="context-menu-separator" role="separator"></div>
      {:else}
        <button
          class="context-menu-item"
          class:danger={item.danger}
          on:click={() => handleAction(item.action || '')}
          role="menuitem"
          tabindex="-1"
        >
          <span class="menu-icon" aria-hidden="true">{item.icon}</span>
          <span class="menu-text">{$t(item.labelKey || '')}</span>
          {#if item.shortcut}
            <span class="menu-shortcut" aria-hidden="true">{item.shortcut}</span>
          {/if}
        </button>
      {/if}
    {/each}
  </div>
{/if}

<style>
  .context-menu {
    position: fixed;
    background: var(--color-bg-secondary, #383838);
    border: 1px solid var(--color-border, #555);
    border-radius: 6px;
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.1);
    z-index: 3000;
    min-width: 200px;
    padding: 4px 0;
    font-size: 13px;
    user-select: none;
    animation: contextMenuAppear 0.15s ease-out;
  }

  .context-menu-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    gap: 8px;
    color: var(--color-text-primary, #e8e8e8);
    width: 100%;
    border: none;
    background: transparent;
    text-align: left;
    font-size: 13px;
  }

  .context-menu-item:hover,
  .context-menu-item:focus {
    background: var(--color-bg-hover, #4a4a4a);
    outline: none;
  }

  .context-menu-item.danger {
    color: var(--color-danger, #ff6b6b);
  }

  .context-menu-item.danger:hover {
    background: rgba(255, 107, 107, 0.1);
  }

  .menu-icon {
    font-size: 14px;
    width: 16px;
    text-align: center;
    flex-shrink: 0;
  }

  .menu-text {
    flex: 1;
  }

  .menu-shortcut {
    font-size: 11px;
    color: var(--color-text-muted, #aaa);
    font-weight: 500;
  }

  .context-menu-separator {
    height: 1px;
    background: var(--color-border, #555);
    margin: 4px 8px;
  }

  @keyframes contextMenuAppear {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-4px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
</style>
